import {APP_KEY} from 'airship/modules/get-url'
import reportError from 'airship/modules/report-error'

import * as cache from '../session-cache'
import {ApiClient, EventPropertiesApiClient} from './ApiClient'
import {AnalyticsDisabledError} from './errors'
import {AppGroupProps, BackendProps, CompanyGroupProps} from './types'

const CACHE_EXPIRY = 6 * cache.Duration.Hour
// getUrl does not work on these pages, probably because iframes:
const DISABLE_ON_URL_FRAGMENTS = [
  '/composer/rich_composer/',
  '/composer/-/richpush/',
]

export interface AnalyticsBackend {
  appKey: string | undefined
  getEventProperties(): Promise<BackendProps | null>
  getGroupProperties(): Promise<{
    company: CompanyGroupProps | null
    app: AppGroupProps | null
  } | null>
}

export class Backend implements AnalyticsBackend {
  public appKey: string | undefined
  apiClient: EventPropertiesApiClient

  public constructor(
    appKey: string | undefined,
    apiClient: EventPropertiesApiClient
  ) {
    this.appKey = appKey || ''
    this.apiClient = apiClient
  }

  public async getEventProperties(): Promise<BackendProps | null> {
    const pathname = window.location.pathname
    const cacheKey = `event-properties-${this.appKey}-${pathname}`

    if (shouldIgnorePage(pathname)) {
      return null
    }

    let props: BackendProps | null | undefined =
      cache.get<BackendProps>(cacheKey)

    if (props) {
      return props
    }

    const isFlightDeck = pathname.startsWith('/admin/flight_deck')
    if (this.appKey && !isFlightDeck) {
      props = await this.getAppEventProperties(pathname)
    } else {
      props = await this.getNonAppEventProperties(pathname)
    }

    if (props) {
      cache.set(cacheKey, props, CACHE_EXPIRY)
    }

    return props
  }

  public async getGroupProperties(): Promise<{
    company: CompanyGroupProps | null
    app: AppGroupProps | null
  } | null> {
    const backendProps = await this.getEventProperties()
    const eventProps = backendProps?.event
    if (!eventProps) {
      return null
    }

    // For now, we'll base the user's group on whatever app they are working
    // with. This means a user could be in multiple groups. Segment supports
    // this (see linked docs above).
    let company: CompanyGroupProps | null = null
    if (eventProps.appCompanyId && eventProps.appCompanyName) {
      company = {
        companyId: eventProps.appCompanyId,
        companyName: eventProps.appCompanyName,
        companyBillingPlan: eventProps.appBillingPlan,
        companyEcommerceTier: eventProps.appCompanyEcommerceTier,
        companyGroups: eventProps.appCompanyGroups,
      }
    }

    let app: AppGroupProps | null = null
    if (eventProps.appKey) {
      app = {
        appIndustry: eventProps.appIndustry,
        appKey: eventProps.appKey,
        appName: eventProps.appName,
        appSubIndustry: eventProps.appSubIndustry,
        appType: eventProps.appType,
        companyId: eventProps.appCompanyId,
        companyName: eventProps.appCompanyName,
      }
    }

    return {
      company,
      app,
    }
  }

  /**
   * Return "group" (aka "account") properties for the user.
   */
  async getAppEventProperties(pathname: string): Promise<BackendProps | null> {
    let props: BackendProps | null | undefined = null

    try {
      props = await this.apiClient.fetchAppEventProperties(pathname)
    } catch (err) {
      if (err instanceof AnalyticsDisabledError) {
        // if analytics are disabled, let the error propagate
        throw err
      }
      reportError(err)
    }

    return props || null
  }

  async getNonAppEventProperties(
    pathname: string
  ): Promise<BackendProps | null> {
    let props: BackendProps | null | undefined = null

    try {
      props = await this.apiClient.fetchNonAppEventProperties(pathname)
    } catch (err) {
      if (err instanceof AnalyticsDisabledError) {
        // if analytics are disabled, let the error propagate
        throw err
      }
      reportError(err)
    }

    return props || null
  }
}

export function getBackend(): Backend {
  const appKey = getAppKey()
  return new Backend(appKey, new ApiClient(appKey))
}

function getAppKey(): string | undefined {
  return APP_KEY && APP_KEY.length === 22 ? APP_KEY : undefined
}

function shouldIgnorePage(pathname: string): boolean {
  for (const fragment of DISABLE_ON_URL_FRAGMENTS) {
    if (pathname && pathname.includes(fragment)) {
      return true
    }
  }

  return false
}
